import React, { useState, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

gsap.registerPlugin(ScrollTrigger);

const {
  FiCode, FiCpu, FiLayout, FiCloud,
  FiGithub, FiLinkedin, FiUpload, FiArrowRight,
  FiCheck, FiUsers, FiGlobe, FiZap, FiChevronDown,
  FiLoader
} = FiIcons;

const roles = [
  {
    icon: FiCode,
    title: ".NET / Backend Engineer",
    type: "Full-time",
    location: "Remote / Hybrid",
    tags: [".NET", "C#", "REST APIs", "SQL"],
    desc: "Build robust, scalable backend systems for enterprise clients. You care about clean architecture and testable code."
  },
  {
    icon: FiCpu,
    title: "AI / ML Engineer",
    type: "Full-time",
    location: "Remote / Hybrid",
    tags: ["Python", "TensorFlow", "PyTorch", "MLOps"],
    desc: "Design and deploy machine learning models that solve real business problems — not research projects that gather dust."
  },
  {
    icon: FiLayout,
    title: "Frontend Developer",
    type: "Full-time",
    location: "Remote / Hybrid",
    tags: ["React", "TypeScript", "Tailwind", "Motion"],
    desc: "Craft interfaces that are fast, accessible, and genuinely beautiful. You sweat the details that most people miss."
  },
  {
    icon: FiCloud,
    title: "Cloud / DevOps Engineer",
    type: "Full-time",
    location: "Remote / Hybrid",
    tags: ["Azure", "AWS", "CI/CD", "Terraform"],
    desc: "Own the infrastructure layer — from deployment pipelines to cloud cost optimisation and reliability engineering."
  }
];

const perks = [
  { icon: FiGlobe, title: "Remote-first", desc: "Work from anywhere. We care about output, not office hours." },
  { icon: FiZap, title: "Interesting work", desc: "No CRUD apps for the sake of it. Real AI and engineering challenges." },
  { icon: FiUsers, title: "Small, senior team", desc: "No bureaucracy. Your decisions ship and your voice is heard." },
  { icon: FiCheck, title: "Flexible hours", desc: "Deep work requires uninterrupted focus. We build around that." }
];

const ROLE_OPTIONS = [
  ".NET / Backend Engineer",
  "AI / ML Engineer",
  "Frontend Developer",
  "Cloud / DevOps Engineer",
  "Other"
];

// ─── Web3Forms access key ───────────────────────────────────────────────────
// 1. Go to https://web3forms.com
// 2. Enter patelvir@gmail.com and click "Create Access Key"
// 3. Paste the key you receive below (it's public-safe)
const WEB3FORMS_ACCESS_KEY = "767163dd-b69f-48a0-9dcf-da28db950446"; // ← replace this

const Careers = () => {
  const [openRole, setOpenRole] = useState(null);

  const heroRef  = useRef(null);
  const perksRef = useRef([]);
  const quoteRef = useRef(null);
  const rolesRef = useRef([]);
  const formRef  = useRef(null);
  const push = (b) => (el) => { if (el) b.current.push(el); };

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Hero slides from left
      gsap.fromTo(heroRef.current,
        { autoAlpha: 0, x: -36 },
        { autoAlpha: 1, x: 0, duration: 0.9, ease: 'expo.out' }
      );
      // Perks stagger
      const perks = perksRef.current.filter(Boolean);
      gsap.fromTo(perks,
        { autoAlpha: 0, y: 28 },
        { autoAlpha: 1, y: 0, duration: 0.65, ease: 'power3.out',
          stagger: { amount: 0.35, from: 'start' },
          scrollTrigger: { trigger: perks[0], start: 'top 84%', once: true } }
      );
      // Quote scale
      gsap.fromTo(quoteRef.current,
        { autoAlpha: 0, scale: 0.88 },
        { autoAlpha: 1, scale: 1, duration: 1.1, ease: 'expo.out',
          scrollTrigger: { trigger: quoteRef.current, start: 'top 80%', once: true } }
      );
      // Role rows stagger
      const rows = rolesRef.current.filter(Boolean);
      gsap.fromTo(rows,
        { autoAlpha: 0, x: -24 },
        { autoAlpha: 1, x: 0, duration: 0.6, ease: 'power3.out',
          stagger: { amount: 0.3, from: 'start' },
          scrollTrigger: { trigger: rows[0], start: 'top 85%', once: true } }
      );
      // Application form
      gsap.fromTo(formRef.current,
        { autoAlpha: 0, y: 32 },
        { autoAlpha: 1, y: 0, duration: 0.9, ease: 'power3.out',
          scrollTrigger: { trigger: formRef.current, start: 'top 85%', once: true } }
      );
    });
    return () => ctx.revert();
  }, []);
  const [form, setForm] = useState({
    name: '', email: '', role: '', github: '', linkedin: '', message: '', cv: null
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [cvName, setCvName] = useState('');

  // ── Smooth scroll to the apply form ──────────────────────────────────────
  const scrollToApply = (roleTitle = null) => {
    if (roleTitle) {
      setForm(prev => ({ ...prev, role: roleTitle }));
    }
    const el = document.getElementById('apply');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (file) {
      setForm(prev => ({ ...prev, cv: file }));
      setCvName(file.name);
    }
  };

  // ── Submit via Web3Forms ──────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const data = new FormData();
      data.append('access_key', WEB3FORMS_ACCESS_KEY);
      data.append('subject', `New Application: ${form.role || 'General'} — ${form.name}`);
      data.append('name', form.name);
      data.append('email', form.email);
      data.append('role', form.role);
      data.append('github', form.github);
      data.append('linkedin', form.linkedin);
      data.append('message', form.message || '(no message provided)');

      // Attach CV if provided (Web3Forms supports file attachments)
      if (form.cv) {
        data.append('attachment', form.cv);
      }

      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: data,
      });

      const json = await res.json();

      if (json.success) {
        setSubmitted(true);
      } else {
        setError(json.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-32 pb-0 relative overflow-hidden bg-white">
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.15]"></div>

      {/* ── Hero ── */}
      <section className="max-w-7xl mx-auto px-6 relative z-10 mb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div ref={heroRef} style={{ opacity: 0 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-50 border border-cyan-200 mb-6">
              <div className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse"></div>
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-600">We're Hiring</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-slate-900 mb-8 leading-tight">
              Build the Future <br />
              <span className="text-gradient-cyan">With Us.</span>
            </h1>
            <p className="text-slate-600 text-lg leading-relaxed mb-6">
              We're a small, senior team that ships real AI and engineering products. If you care deeply about your craft and want your work to actually matter — read on.
            </p>
            <p className="text-slate-600 text-lg leading-relaxed">
              We hire for character and competence. Degrees are irrelevant. Your GitHub and what you've shipped are everything.
            </p>
          </div>

          <div className="relative">
            <div className="absolute inset-0 bg-cyan-100/40 blur-[100px] rounded-full animate-pulse"></div>
            <div className="relative aspect-square bg-white border border-slate-200 p-12 flex items-center justify-center overflow-hidden shadow-sm">
              <div className="absolute inset-0 opacity-20">
                <div className="absolute top-0 left-0 w-full h-full border-[1px] border-cyan-300 transform rotate-45 scale-150"></div>
                <div className="absolute top-0 left-0 w-full h-full border-[1px] border-blue-300 transform -rotate-12 scale-125"></div>
              </div>
              <div className="grid grid-cols-2 gap-6 relative z-10">
                {[FiCode, FiCpu, FiLayout, FiCloud].map((Icon, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.15 }}
                    className="w-16 h-16 bg-cyan-50 border border-cyan-100 flex items-center justify-center"
                  >
                    <SafeIcon icon={Icon} className="text-2xl text-cyan-500" />
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Perks ── */}
      <section className="py-24 bg-slate-50 border-y border-slate-100 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row gap-12 items-start mb-16">
            <div className="md:w-1/3">
              <div className="flex items-center gap-4 mb-4">
                <SafeIcon icon={FiUsers} className="text-3xl text-cyan-600" />
                <h2 className="text-2xl font-bold text-slate-900 uppercase tracking-wider">Why Aetrix</h2>
              </div>
              <div className="h-1 w-20 bg-cyan-500"></div>
            </div>
            <div className="md:w-2/3">
              <p className="text-slate-700 text-xl leading-relaxed">
                We're not a startup chasing valuations. We're engineers building tools that actually work, for businesses that actually need them.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {perks.map((perk, i) => (
              <div
                key={i}
                ref={push(perksRef)}
                style={{ opacity: 0 }}
                className="p-8 bg-white border border-slate-200 hover:border-cyan-300 transition-all group shadow-sm hover:shadow-md"
              >
                <div className="w-12 h-12 bg-cyan-50 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                  <SafeIcon icon={perk.icon} className="text-xl text-cyan-600" />
                </div>
                <h3 className="text-slate-900 font-bold mb-2">{perk.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{perk.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Quote callout ── */}
      <section className="py-32 relative z-10 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div ref={quoteRef} style={{ opacity: 0 }} className="relative inline-block">
            <div className="absolute -top-12 -left-12 text-9xl text-cyan-200 font-serif leading-none">"</div>
            <h2 className="text-3xl md:text-5xl font-black text-slate-900 italic relative z-10 px-4">
              We don't hire for roles — <br />
              <span className="text-gradient-cyan">we hire for craft.</span>
            </h2>
            <div className="absolute -bottom-12 -right-12 text-9xl text-cyan-200 font-serif leading-none rotate-180">"</div>
          </div>
        </div>
      </section>

      {/* ── Open Roles ── */}
      <section className="py-24 bg-slate-50 border-y border-slate-100 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Open Positions</h2>
            <p className="text-slate-500 uppercase tracking-widest text-xs font-bold">Current opportunities</p>
          </div>
          <div className="space-y-4">
            {roles.map((role, i) => (
              <div
                key={i}
                ref={push(rolesRef)}
                style={{ opacity: 0 }}
                className="bg-white border border-slate-200 hover:border-cyan-300 transition-all shadow-sm overflow-hidden"
              >
                {/* Role header — clickable accordion */}
                <button
                  onClick={() => setOpenRole(openRole === i ? null : i)}
                  className="w-full flex items-center gap-6 p-6 md:p-8 text-left group"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-cyan-50 border border-cyan-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <SafeIcon icon={role.icon} className="text-xl text-cyan-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3 mb-1">
                      <h3 className="text-slate-900 font-bold text-lg">{role.title}</h3>
                      <span className="px-2 py-0.5 bg-cyan-50 border border-cyan-100 text-cyan-600 text-[10px] font-black uppercase tracking-widest">{role.type}</span>
                      <span className="text-slate-400 text-xs font-medium">{role.location}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {role.tags.map((tag, t) => (
                        <span key={t} className="text-[10px] font-bold uppercase tracking-widest text-slate-400 border border-slate-100 px-2 py-0.5 bg-slate-50">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <SafeIcon
                    icon={FiChevronDown}
                    className={`text-xl text-slate-400 flex-shrink-0 transition-transform duration-300 ${openRole === i ? 'rotate-180 text-cyan-500' : ''}`}
                  />
                </button>

                {/* Accordion body */}
                <AnimatePresence initial={false}>
                  {openRole === i && (
                    <motion.div
                      key="body"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 md:px-8 pb-8 border-t border-slate-100 pt-6">
                        <p className="text-slate-600 leading-relaxed mb-6">{role.desc}</p>
                        {/* ── Smooth scroll button (replaces plain anchor) ── */}
                        <button
                          onClick={() => scrollToApply(role.title)}
                          className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-500 text-white font-black uppercase tracking-widest text-xs hover:bg-cyan-400 transition-all"
                        >
                          Apply for this role
                          <SafeIcon icon={FiArrowRight} />
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Application Form ── */}
      <section id="apply" className="py-24 relative z-10 scroll-mt-24">
        <div ref={formRef} style={{ opacity: 0 }} className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Apply Now</h2>
            <p className="text-slate-500 text-sm leading-relaxed max-w-xl mx-auto">
              Don't see a perfect fit but think you belong here? Apply anyway. We hire the person, not just the role.
            </p>
          </div>

          <AnimatePresence mode="wait">
            {submitted ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white border border-cyan-200 p-16 text-center shadow-sm relative overflow-hidden"
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-cyan-500"></div>
                <div className="w-16 h-16 bg-cyan-50 border border-cyan-200 flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={FiCheck} className="text-3xl text-cyan-500" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Application Received</h3>
                <p className="text-slate-600 leading-relaxed">
                  Thanks for reaching out. We review every application personally and will be in touch if there's a strong fit.
                </p>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                onSubmit={handleSubmit}
                className="bg-white border border-slate-200 p-8 md:p-12 shadow-sm relative overflow-hidden"
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-cyan-500"></div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Name */}
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Jane Smith"
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 text-slate-900 text-sm placeholder:text-slate-300 focus:outline-none focus:border-cyan-400 transition-colors"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      placeholder="jane@example.com"
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 text-slate-900 text-sm placeholder:text-slate-300 focus:outline-none focus:border-cyan-400 transition-colors"
                    />
                  </div>
                </div>

                {/* Role */}
                <div className="mb-6">
                  <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Role You're Applying For *</label>
                  <div className="relative">
                    <select
                      name="role"
                      required
                      value={form.role}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 text-slate-900 text-sm appearance-none focus:outline-none focus:border-cyan-400 transition-colors cursor-pointer"
                    >
                      <option value="" disabled>Select a role…</option>
                      {ROLE_OPTIONS.map(r => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                    <SafeIcon icon={FiChevronDown} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* GitHub */}
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">GitHub Profile *</label>
                    <div className="relative">
                      <SafeIcon icon={FiGithub} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-lg" />
                      <input
                        type="url"
                        name="github"
                        required
                        value={form.github}
                        onChange={handleChange}
                        placeholder="https://github.com/username"
                        className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 text-slate-900 text-sm placeholder:text-slate-300 focus:outline-none focus:border-cyan-400 transition-colors"
                      />
                    </div>
                  </div>

                  {/* LinkedIn */}
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">LinkedIn Profile *</label>
                    <div className="relative">
                      <SafeIcon icon={FiLinkedin} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-lg" />
                      <input
                        type="url"
                        name="linkedin"
                        required
                        value={form.linkedin}
                        onChange={handleChange}
                        placeholder="https://linkedin.com/in/username"
                        className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 text-slate-900 text-sm placeholder:text-slate-300 focus:outline-none focus:border-cyan-400 transition-colors"
                      />
                    </div>
                  </div>
                </div>

                {/* CV Upload */}
                <div className="mb-6">
                  <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">CV / Résumé *</label>
                  <label className="flex items-center gap-4 px-4 py-4 bg-slate-50 border border-slate-200 border-dashed hover:border-cyan-300 hover:bg-cyan-50/30 transition-all cursor-pointer group">
                    <div className="w-10 h-10 bg-white border border-slate-200 flex items-center justify-center flex-shrink-0 group-hover:border-cyan-300 transition-colors">
                      <SafeIcon icon={FiUpload} className="text-slate-400 group-hover:text-cyan-500 transition-colors" />
                    </div>
                    <div>
                      <p className="text-slate-700 text-sm font-bold">
                        {cvName ? cvName : 'Click to upload your CV'}
                      </p>
                      <p className="text-slate-400 text-xs">PDF or DOCX, max 5MB</p>
                    </div>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFile}
                      required
                      className="hidden"
                    />
                  </label>
                </div>

                {/* Message */}
                <div className="mb-8">
                  <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">
                    Tell Us About Yourself
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={5}
                    placeholder="What have you built that you're proud of? What problems excite you? Why Aetrix Logic?"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 text-slate-900 text-sm placeholder:text-slate-300 focus:outline-none focus:border-cyan-400 transition-colors resize-none"
                  />
                </div>

                {/* Error message */}
                {error && (
                  <div className="mb-6 px-4 py-3 bg-red-50 border border-red-200 text-red-600 text-sm">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-5 bg-slate-900 text-white font-black uppercase tracking-widest text-xs hover:bg-black transition-all flex items-center justify-center gap-3 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <span>Submitting…</span>
                      <SafeIcon icon={FiLoader} className="text-lg animate-spin" />
                    </>
                  ) : (
                    <>
                      <span>Submit Application</span>
                      <SafeIcon icon={FiArrowRight} className="text-lg" />
                    </>
                  )}
                </button>

                <p className="text-center text-slate-400 text-xs mt-6">
                  We review every application personally. You'll hear from us if there's a strong fit.
                </p>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </section>

      <div className="h-24"></div>
    </div>
  );
};

export default Careers;